import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import {
  queuePositionSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'

const Context = createContext<
  | (ReturnType<typeof useDraggable> & {
      position: { x: number; y: number }
      style?: React.CSSProperties
    })
  | undefined
>(undefined)

interface DraggableProviderProps extends PropsWithChildren {
  position: { x: number; y: number }
}

const DraggableProvider: FC<DraggableProviderProps> = ({
  children,
  position,
}) => {
  const uiState = useStore(uiStateSelector)
  const queuePosition = useStore(queuePositionSelector)
  const draggable = useDraggable({
    id: 'draggable',
  })

  const style = useMemo(() => {
    if (
      uiState === 'on_call_minimized' ||
      ((uiState === 'dialing_minimized' || uiState === 'idle_minimized') &&
        queuePosition)
    ) {
      return draggable.transform
        ? {
            transform: `translate3d(${(position?.x || 0) + draggable.transform.x}px, ${(position?.y || 0) + draggable.transform.y}px, 0)`,
          }
        : {
            transform: `translate3d(${position?.x || 0}px, ${position?.y || 0}px, 0)`,
          }
    }

    return undefined
  }, [draggable.transform, position, uiState, queuePosition])

  return (
    <Context.Provider value={{ ...draggable, position, style }}>
      {children}
    </Context.Provider>
  )
}

/**
 * Draggable context provider with dndkit configuration
 */
export const DraggableContext: FC<PropsWithChildren> = ({ children }) => {
  const activationConstraint = {
    delay: 100,
    tolerance: 5,
  }
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint,
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint,
  })
  const sensors = useSensors(mouseSensor, touchSensor)

  const [position, setPosition] = useState({ x: 0, y: 0 })
  const onDragEnd = ({ delta }) => {
    setPosition(({ x, y }) => {
      return {
        x: x + delta.x,
        y: y + delta.y,
      }
    })
  }

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      modifiers={[restrictToWindowEdges]}
    >
      <DraggableProvider position={position}>{children}</DraggableProvider>
    </DndContext>
  )
}

/**
 * Hook to access the draggable context
 */
export const useDraggableContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error(
      'useDraggableContext must be used within a DraggableContext',
    )
  }
  return context
}
